import { Injectable, Renderer2, RendererFactory2, ComponentRef, ApplicationRef, Injector, createComponent, EnvironmentInjector, OnDestroy, NgZone } from '@angular/core';
import { TourGuideTooltipService } from './tour-guide-tooltip.service';
import { TooltipConfig } from '../models/tooltip.model';
import { TooltipPosition, TooltipAlignment, TooltipIconMode, TooltipAlignTarget } from '../models/tooltip.enum';
import { TooltipAnchorService } from './tooltip-anchor.service';
import { TourGuideTooltipComponent } from '../components/tour-guide-tooltip/tour-guide-tooltip.component';
import { TooltipZIndexService } from './tooltip-utils.service';
import { BehaviorSubject } from 'rxjs';

interface TooltipInstance {
  config: TooltipConfig;
  questionMarkElement: HTMLElement;
  targetElement: HTMLElement;
  // Element the icon was injected into or next to: the target itself, or the
  // parent of an absolutely positioned <input>/<img> (see iconHost()). Stored so
  // cleanup still finds it after the target has left the DOM.
  iconHost: HTMLElement;
  resizeObserver: ResizeObserver | null;
  // Inline mode on a block-level target: the icon is an absolutely-positioned
  // overlay child of the target instead of an in-flow node, so it doesn't push
  // the target's own content around.
  overlay?: boolean;
  // The icon host carries our position:relative (data-tour-guide-relative) as
  // the overlay's containing block. Several instances can share one host; the
  // last of them to go reverts it.
  restoredTargetPosition?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TooltipInjectorService implements OnDestroy {
  private renderer: Renderer2;
  private tooltipInstances: Map<string, TooltipInstance> = new Map();
  private highlightOverlayElement: HTMLElement | null = null;
  private tooltipComponentRef: ComponentRef<TourGuideTooltipComponent> | null = null;
  private currentPositioningElement: HTMLElement | null = null;
  private currentHighlightedElement: HTMLElement | null = null;
  private currentTooltipConfig: TooltipConfig | null = null;
  private isTooltipOpen: boolean = false;
  // Id of the tooltip whose bubble is (or, if opened now, would be) flipped to
  // the opposite side because the configured position doesn't fit the
  // viewport — null otherwise. Set live by positionTooltip while a tooltip is
  // open, and predicted by checkSmartPosition for the admin editor.
  readonly smartPositionedTooltipId$ = new BehaviorSubject<string | null>(null);
  private hiddenIconElement: HTMLElement | null = null;
  private readonly iconSize = 20;
  // Closest the bubble's arrow may get to either end of its edge: the 12px corner
  // radius plus the arrow's 10px half-width (tour-guide-tooltip.component.scss).
  private readonly tooltipArrowEdgeInset = 22;
  private readonly iconGap = 8;
  private readonly defaultContainerBorderWidth = 1;
  private readonly defaultContainerBorderColor = '#9CA3AF';
  private unlistenWindowResize: (() => void) | null = null;
  private unlistenDocumentScroll: (() => void) | null = null;
  private openTooltipResizeObserver: ResizeObserver | null = null;
  private unlistenOpenTooltipListeners: (() => void)[] = [];
  // Properties whose transition can shift a descendant's getBoundingClientRect()
  // without firing a window resize or a scroll event — e.g. the sidebar/admin
  // toolbar panel expanding animates width/margin-left on an ancestor. Deliberately
  // narrow (no 'transform', the most common :hover-triggered property in this
  // codebase — chevrons, scale effects) so this doesn't fire on every hover
  // flicker caused by the mouse staying still while content scrolls under it.
  private readonly layoutTransitionProperties = new Set([
    'margin-left', 'margin-right', 'margin', 'width', 'left', 'right'
  ]);
  // Appending the icon inside these renders nothing, so they can't be the
  // containing block for an overlay icon and keep the in-flow fallback.
  private readonly nonHostingTags = new Set([
    'INPUT', 'IMG', 'BR', 'HR', 'AREA', 'BASE', 'COL', 'EMBED', 'LINK', 'META',
    'PARAM', 'SOURCE', 'TRACK', 'WBR', 'TEXTAREA', 'SELECT', 'IFRAME', 'CANVAS'
  ]);
  private activeLayoutTransitions = 0;
  private unlistenTransitionRun: (() => void) | null = null;
  private unlistenTransitionEnd: (() => void) | null = null;
  // Single per-frame tracking loop shared by scroll, resize and layout-transition
  // signals. A raw scroll/resize listener that just debounces-and-fires-once
  // makes tracked icons sit frozen while the user is actively scrolling and only
  // snap into place a beat after they stop — this instead keeps repositioning
  // every frame for as long as movement was recently signalled.
  private trackingRafId: number | null = null;
  private lastMovementSignalAt = 0;
  private trackingLoopStartedAt = 0;
  private readonly movementSettleWindowMs = 150;
  // Safety valve: a missed/cancelled transitionend (e.g. element removed
  // mid-transition) shouldn't leave the tracking loop running forever.
  private readonly maxTrackingLoopDurationMs = 5000;

  constructor(
    rendererFactory: RendererFactory2,
    private tooltipService: TourGuideTooltipService,
    private appRef: ApplicationRef,
    private injector: Injector,
    private environmentInjector: EnvironmentInjector,
    private anchorService: TooltipAnchorService,
    private zIndex: TooltipZIndexService,
    private ngZone: NgZone
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.initialize();
  }

  private initialize(): void {
    this.tooltipService.tooltips$.subscribe(() => this.updateAllTooltips());
    // Scroll, resize and transition events fire constantly, and the rAF
    // tracking loop they start runs every frame — inside the zone each one
    // would trigger app-wide change detection (every CSS transition anywhere,
    // hover effects included). The loop only touches the DOM directly, so it
    // doesn't need the zone; anything that does (smartPositionedTooltipId$
    // subscribers) re-enters it itself.
    this.ngZone.runOutsideAngular(() => {
      this.startViewportObserver();
      this.startLayoutTransitionObserver();
    });
  }

  private startViewportObserver(): void {
    if (this.unlistenWindowResize) return;

    this.unlistenWindowResize = this.renderer.listen('window', 'resize', () => this.markMovementActive());

    // The real scroll container is .app-content (see app.component.scss), not
    // window/document, and 'scroll' doesn't bubble — capture phase is the only
    // way to hear it from here, same as the admin picker's hover-outline does.
    const onScroll = () => this.markMovementActive();
    document.addEventListener('scroll', onScroll, true);
    this.unlistenDocumentScroll = () => document.removeEventListener('scroll', onScroll, true);
  }

  private markMovementActive(): void {
    this.lastMovementSignalAt = performance.now();
    this.ensureTrackingLoopRunning();
  }

  // Shared by scroll/resize (markActive) and the layout-transition observer
  // (activeLayoutTransitions). Keeps repositioning every animation frame while
  // either signal is live, and lets itself stop shortly after both go quiet.
  private ensureTrackingLoopRunning(): void {
    if (this.trackingRafId !== null) return;
    if (!this.hasAnythingToTrack()) return;

    this.trackingLoopStartedAt = performance.now();

    const step = () => {
      this.repositionAllIcons();
      if (this.isTooltipOpen) this.repositionOpenTooltip();

      const stillMoving = performance.now() - this.lastMovementSignalAt < this.movementSettleWindowMs;
      const transitionTimedOut = performance.now() - this.trackingLoopStartedAt > this.maxTrackingLoopDurationMs;
      if (transitionTimedOut) this.activeLayoutTransitions = 0;

      if (stillMoving || this.activeLayoutTransitions > 0) {
        this.trackingRafId = requestAnimationFrame(step);
      } else {
        this.trackingRafId = null;
      }
    };

    this.trackingRafId = requestAnimationFrame(step);
  }

  // Inline icons move with the page on their own (positionQuestionMarkIcon is a
  // no-op for them) — only Positioned icons and an open bubble need the loop.
  private hasAnythingToTrack(): boolean {
    if (this.isTooltipOpen) return true;
    for (const instance of this.tooltipInstances.values()) {
      if ((instance.config.iconPlacementMode ?? TooltipIconMode.Inline) === TooltipIconMode.Positioned) return true;
    }
    return false;
  }

  private stopTrackingLoop(): void {
    if (this.trackingRafId !== null) {
      cancelAnimationFrame(this.trackingRafId);
      this.trackingRafId = null;
    }
    this.activeLayoutTransitions = 0;
  }

  // Collapsing/expanding the sidebar or the admin toolbar panel doesn't resize
  // or scroll anything — it animates width/margin-left with a CSS transition on
  // an ancestor of the anchored content. Track that directly with a short-lived
  // rAF loop instead of waiting for the next real scroll/resize to catch up.
  private startLayoutTransitionObserver(): void {
    if (this.unlistenTransitionRun) return;

    const isLayoutProperty = (propertyName: string) => this.layoutTransitionProperties.has(propertyName);

    const onTransitionRun = (event: TransitionEvent) => {
      if (!isLayoutProperty(event.propertyName)) return;
      this.activeLayoutTransitions++;
      this.ensureTrackingLoopRunning();
    };

    const onTransitionEnd = (event: TransitionEvent) => {
      if (!isLayoutProperty(event.propertyName)) return;
      this.activeLayoutTransitions = Math.max(0, this.activeLayoutTransitions - 1);
    };

    document.addEventListener('transitionrun', onTransitionRun, true);
    document.addEventListener('transitionend', onTransitionEnd, true);
    document.addEventListener('transitioncancel', onTransitionEnd, true);
    this.unlistenTransitionRun = () => document.removeEventListener('transitionrun', onTransitionRun, true);
    this.unlistenTransitionEnd = () => {
      document.removeEventListener('transitionend', onTransitionEnd, true);
      document.removeEventListener('transitioncancel', onTransitionEnd, true);
    };
  }

  private stopLayoutTransitionObserver(): void {
    if (this.unlistenTransitionRun) {
      this.unlistenTransitionRun();
      this.unlistenTransitionRun = null;
    }
    if (this.unlistenTransitionEnd) {
      this.unlistenTransitionEnd();
      this.unlistenTransitionEnd = null;
    }
  }

  private updateAllTooltips(): void {
    const tooltips = this.tooltipService.getAllTooltips().filter(t => t.isEnabled !== false);
    if (tooltips.length === 0) { this.removeAllIcons(); return; }

    const currentIds = new Set(tooltips.map(t => t.id));

    this.tooltipInstances.forEach((instance, id) => {
      if (!currentIds.has(id)) {
        this.removeTooltipInstance(id);
        this.tooltipInstances.delete(id);
      }
    });

    tooltips.forEach(config => {
      if (this.isTooltipOpen && this.currentTooltipConfig?.id === config.id) {
        this.currentTooltipConfig = config;
        this.refreshOpenTooltip(config);
      }

      const existing = this.tooltipInstances.get(config.id);
      if (existing) {
        if (!existing.targetElement.isConnected) {
          // Target element was removed by navigation — drop the stale instance
          this.removeIconOnly(config.id);
          this.tooltipInstances.delete(config.id);
          this.injectIfMarked(config.id);
          return;
        }
        const oldLabel = existing.config.iconLabel ?? '?';
        const newLabel = config.iconLabel ?? '?';
        if (oldLabel !== newLabel) {
          this.renderer.setProperty(existing.questionMarkElement, 'textContent', newLabel);
        }

        const oldMode = existing.config.iconPlacementMode ?? TooltipIconMode.Inline;
        const newMode = config.iconPlacementMode ?? TooltipIconMode.Inline;
        if (oldMode !== newMode) {
          this.removeIconOnly(config.id);
          this.tooltipInstances.delete(config.id);
          this.injectIfMarked(config.id);
        } else if (newMode === TooltipIconMode.Inline) {
          if (existing.overlay !== this.shouldOverlayInlineIcon(existing.iconHost, config)) {
            // Crossed between overlay and in-flow placement (e.g. Right -> Top on
            // a text target) — the icon's DOM position changes, so re-inject.
            this.removeIconOnly(config.id);
            this.tooltipInstances.delete(config.id);
            this.injectIfMarked(config.id);
          } else if (existing.overlay) {
            // Overlay icon lives at a fixed DOM position (child of the target)
            // and is placed purely with CSS — every placement/align/offset
            // change is just a restyle, never a re-inject.
            existing.config = config;
            this.applyInlineStyles(existing.questionMarkElement, config, existing.iconHost);
          } else {
            const oldSide = this.inlineSide(existing.config.iconPlacement);
            const newSide = this.inlineSide(config.iconPlacement);
            existing.config = config;
            if (oldSide !== newSide) {
              // Placement side flipped — re-inject to move the DOM node
              this.removeIconOnly(config.id);
              this.tooltipInstances.delete(config.id);
              this.injectIfMarked(config.id);
            } else {
              this.applyInlineStyles(existing.questionMarkElement, config);
            }
          }
        } else {
          existing.config = config;
          this.positionQuestionMarkIcon(existing.questionMarkElement, existing.targetElement, config);
        }
      } else {
        this.injectIfMarked(config.id);
      }
    });
  }

  clearDetachedInstances(): void {
    this.tooltipInstances.forEach((instance, id) => {
      if (!instance.targetElement.isConnected) {
        if (instance.resizeObserver) {
          instance.resizeObserver.disconnect();
          instance.resizeObserver = null;
        }
        if (instance.questionMarkElement.parentNode) {
          this.renderer.removeChild(instance.questionMarkElement.parentNode, instance.questionMarkElement);
        }
        this.revertTargetPosition(instance);
        this.tooltipInstances.delete(id);
      }
    });
  }

  private removeAllIcons(): void {
    this.tooltipInstances.forEach((_, id) => this.removeTooltipInstance(id));
    this.tooltipInstances.clear();
  }

  private removeIconOnly(id: string): void {
    const instance = this.tooltipInstances.get(id);
    if (!instance) return;
    if (instance.resizeObserver) {
      instance.resizeObserver.disconnect();
      instance.resizeObserver = null;
    }
    if (instance.questionMarkElement.parentNode) {
      this.renderer.removeChild(instance.questionMarkElement.parentNode, instance.questionMarkElement);
    }
    this.revertTargetPosition(instance);
    // Intentionally keep data-tooltip-id on targetElement so injectIfMarked can recreate the icon
  }

  // Re-keys a tracked instance (e.g. pending:<uuid> -> the real backend id) without
  // touching its DOM. Must be called BEFORE the tooltips$ emission that carries the
  // new id, so updateAllTooltips()'s cleanup loop finds the instance already under
  // its new key instead of treating the old key's disappearance as a deletion.
  renameInstanceId(oldId: string, newId: string): void {
    const instance = this.tooltipInstances.get(oldId);
    if (!instance) return;
    this.tooltipInstances.delete(oldId);
    this.tooltipInstances.set(newId, instance);
  }

  injectIfMarked(tooltipId: string): void {
    // Exclude our own injected icon — it carries the same data-tooltip-id as the
    // real anchor, and if a leftover icon from before a mode switch happens to sit
    // earlier in the DOM, an unrestricted query here would grab the icon itself
    // instead of the anchor.
    const targetEl = document.querySelector(`[data-tooltip-id="${tooltipId}"]:not(.tour-guide-question-icon)`) as HTMLElement | null;
    if (!targetEl) return;

    const config = this.tooltipService.getTooltipById(tooltipId);
    if (!config || config.isEnabled === false) return;

    // Check for an existing icon — but ignore it if it's orphaned (target was navigated away)
    const existingInstance = this.tooltipInstances.get(tooltipId);
    if (existingInstance) {
      if (existingInstance.targetElement.isConnected) return; // already injected and alive
      // Stale instance — clean it up before re-injecting
      this.removeIconOnly(tooltipId);
      this.tooltipInstances.delete(tooltipId);
    } else if (document.querySelector(`.tour-guide-question-icon[data-tooltip-id="${tooltipId}"]`)) {
      // Orphaned icon in DOM not tracked by our map — remove it
      const orphan = document.querySelector(`.tour-guide-question-icon[data-tooltip-id="${tooltipId}"]`) as HTMLElement;
      orphan.parentNode?.removeChild(orphan);
    }

    this.injectTooltip(config, targetEl);
  }

  private injectTooltip(config: TooltipConfig, targetElement: HTMLElement): void {
    try {
      if (this.tooltipInstances.has(config.id)) return;

      const host = this.iconHost(targetElement);
      const questionMark = this.createQuestionMarkIcon(config, targetElement, host);

      const overlay = this.shouldOverlayInlineIcon(host, config);

      const instance: TooltipInstance = {
        config,
        questionMarkElement: questionMark,
        targetElement,
        iconHost: host,
        resizeObserver: null,
        overlay,
        restoredTargetPosition: overlay && host.hasAttribute('data-tour-guide-relative')
      };

      this.tooltipInstances.set(config.id, instance);
      this.startIconTracking(instance);
    } catch (error) {
      console.error('Error injecting tooltip:', error);
    }
  }

  private createQuestionMarkIcon(config: TooltipConfig, targetElement: HTMLElement, host: HTMLElement): HTMLElement {
    const questionMark = this.renderer.createElement('span');
    this.renderer.addClass(questionMark, 'tour-guide-question-icon');
    this.renderer.setProperty(questionMark, 'textContent', config.iconLabel ?? '?');
    this.renderer.setAttribute(questionMark, 'data-tooltip-id', config.id);

    const placementMode = config.iconPlacementMode ?? TooltipIconMode.Inline;

    this.applyIconSize(questionMark, config);

    if (placementMode === TooltipIconMode.Inline) {
      const placement = config.iconPlacement ?? TooltipPosition.Right;

      this.renderer.setStyle(questionMark, 'z-index', String(this.zIndex.QUESTION_MARK));

      const insertBefore = placement === TooltipPosition.Left || placement === TooltipPosition.Top;

      if (this.shouldOverlayInlineIcon(host, config)) {
        // Layout container (card / wrapper / button row), or any Top/Bottom
        // placement: overlay the icon so it takes no layout space and can't push
        // the target's own content around.
        this.applyOverlayIcon(questionMark, host, config);
        this.renderer.appendChild(host, questionMark);
      } else {
        // Text element (heading / paragraph / label) or a genuinely inline
        // target: keep the icon in the flow, trailing the text — the original
        // behaviour. Styled through the same helper the update path uses, so a
        // freshly injected icon and a restyled one can't drift apart.
        this.renderer.setStyle(questionMark, 'position', 'relative');
        this.applyInlineStyles(questionMark, config);

        if (this.isBlockLevelDisplay(host) && !this.nonHostingTags.has(host.tagName)) {
          // Block text element (h2 / p): put the icon inside, trailing the text.
          // A block-level <input>/<img> can't host it and falls through to the
          // sibling insertion below.
          if (insertBefore) {
            this.renderer.insertBefore(host, questionMark, host.firstChild ?? null);
          } else {
            this.renderer.appendChild(host, questionMark);
          }
        } else {
          // Inline element: insert as a sibling next to it.
          const parent = host.parentElement;
          if (parent) {
            if (insertBefore) {
              this.renderer.insertBefore(parent, questionMark, host);
            } else {
              const nextSibling = host.nextSibling;
              if (nextSibling) {
                this.renderer.insertBefore(parent, questionMark, nextSibling);
              } else {
                this.renderer.appendChild(parent, questionMark);
              }
            }
          } else {
            this.renderer.appendChild(host, questionMark);
          }
        }
      }
    } else {
      this.positionQuestionMarkIcon(questionMark, targetElement, config);
      if (targetElement.closest('.modal')) {
        // Lives in <body>, outside the modal: the class keeps the modal-open rule
        // in styles.scss from dropping it under the backdrop with the page's icons.
        this.renderer.addClass(questionMark, 'tour-guide-question-icon-in-modal');
      }
      this.renderer.appendChild(document.body, questionMark);
    }

    this.renderer.listen(questionMark, 'click', (event: Event) => {
      event.stopPropagation();
      // Inside a <label> the browser follows up with a synthetic click on the
      // label's control; that click reaches the document's outside-click
      // listener and closes the tooltip this click just opened.
      event.preventDefault();
      // Don't trust the closed-over `config`/its `.id` — for a tooltip that
      // was `pending:` at icon-creation time, renameInstanceId() re-keys the
      // tracked instance to the real backend id on save, but this closure's
      // `config.id` is a plain string that was never updated, so a lookup by
      // it goes stale forever the moment that rename happens. The DOM
      // attribute IS kept in sync (saveChanges() updates it), and the
      // service's own list is the single source of truth — prefer both over
      // the frozen creation-time snapshot.
      const currentId = questionMark.getAttribute('data-tooltip-id') ?? config.id;
      const freshConfig = this.tooltipService.getTooltipById(currentId)
        ?? this.tooltipInstances.get(currentId)?.config
        ?? config;
      this.showTooltip(freshConfig, targetElement);
    });

    return questionMark;
  }

  private startIconTracking(instance: TooltipInstance): void {
    const placementMode = instance.config.iconPlacementMode ?? TooltipIconMode.Inline;

    if (placementMode === TooltipIconMode.Positioned) {
      requestAnimationFrame(() => {
        this.positionQuestionMarkIcon(instance.questionMarkElement, instance.targetElement, instance.config);
        setTimeout(() => this.positionQuestionMarkIcon(instance.questionMarkElement, instance.targetElement, instance.config), 200);
      });

      if (typeof ResizeObserver !== 'undefined') {
        try {
          const observer = new ResizeObserver(() => {
            this.positionQuestionMarkIcon(instance.questionMarkElement, instance.targetElement, instance.config);
          });
          observer.observe(instance.targetElement);
          instance.resizeObserver = observer;
        } catch (e) {
          console.error('ResizeObserver initialization failed:', e);
        }
      }
    }
  }

  private positionQuestionMarkIcon(
    questionMark: HTMLElement,
    targetElement: HTMLElement,
    config: TooltipConfig
  ): void {
    const placementMode = config.iconPlacementMode ?? TooltipIconMode.Inline;
    if (placementMode === TooltipIconMode.Inline) return;

    const targetRect = targetElement.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const isInViewport = (
      targetRect.bottom > 0 &&
      targetRect.top < viewportHeight &&
      targetRect.right > 0 &&
      targetRect.left < viewportWidth
    );

    if (!isInViewport) {
      this.renderer.setStyle(questionMark, 'display', 'none');
      return;
    } else {
      this.renderer.removeStyle(questionMark, 'display');
    }

    const placement = config.iconPlacement ?? TooltipPosition.Right;
    const align = config.iconAlign ?? TooltipAlignment.Center;
    const rotation = (config.iconRotation ?? 0) % 360;
    const size = config.iconSize ?? this.iconSize;

    let top = 0;
    let left = 0;

    const alignVert = () => {
      if (align === TooltipAlignment.Top) return targetRect.top;
      if (align === TooltipAlignment.Bottom) return targetRect.bottom - size;
      return targetRect.top + (targetRect.height - size) / 2;
    };

    const alignHorz = () => {
      if (align === TooltipAlignment.Left) return targetRect.left;
      if (align === TooltipAlignment.Right) return targetRect.right - size;
      return targetRect.left + (targetRect.width - size) / 2;
    };

    switch (placement) {
      case TooltipPosition.Left:
        left = targetRect.left - size - this.iconGap;
        top = alignVert();
        break;
      case TooltipPosition.Top:
        top = targetRect.top - size - this.iconGap;
        left = alignHorz();
        break;
      case TooltipPosition.Bottom:
        top = targetRect.bottom + this.iconGap;
        left = alignHorz();
        break;
      case TooltipPosition.Right:
      default:
        left = targetRect.right + this.iconGap;
        top = alignVert();
        break;
    }

    top += config.iconOffsetTop ?? 0;
    left += config.iconOffsetLeft ?? 0;

    const maxLeft = Math.max(0, viewportWidth - size);
    const maxTop = Math.max(0, viewportHeight - size);

    top = Math.min(Math.max(0, top), maxTop);
    left = Math.min(Math.max(0, left), maxLeft);

    this.renderer.setStyle(questionMark, 'position', 'fixed');
    this.renderer.setStyle(questionMark, 'top', `${top}px`);
    this.renderer.setStyle(questionMark, 'left', `${left}px`);
    this.renderer.setStyle(questionMark, 'z-index', this.isTooltipOpen ? String(this.zIndex.QUESTION_MARK_WHEN_TOOLTIP_OPEN) : String(this.zIndex.QUESTION_MARK));
    this.applyIconSize(questionMark, config);
    questionMark.style.setProperty('--tour-guide-icon-rotate', `${rotation}deg`);
  }

  // 'before' | 'after' — used to detect when re-injection is needed on placement change
  private inlineSide(placement?: TooltipPosition): 'before' | 'after' {
    return placement === TooltipPosition.Left || placement === TooltipPosition.Top ? 'before' : 'after';
  }

  // An absolutely positioned <input>/<img> is a transparent layer covering its
  // parent (file drop zones, file buttons): it can't hold the icon, and an in-flow
  // sibling lands below the parent's visible box. The icon goes on that parent.
  private iconHost(target: HTMLElement): HTMLElement {
    if (!this.nonHostingTags.has(target.tagName) || !target.parentElement) return target;
    const position = window.getComputedStyle(target).position;
    return position === 'absolute' || position === 'fixed' ? target.parentElement : target;
  }

  private isBlockLevelDisplay(el: HTMLElement): boolean {
    const display = window.getComputedStyle(el).display;
    return display === 'block' || display === 'flex' || display === 'grid'
      || display === 'table' || display === 'table-cell' || display === 'list-item'
      || display === 'table-row' || display === 'table-caption';
  }

  // A "layout container" holds other boxes (cards, wrappers, button rows), as
  // opposed to a text element (a heading/paragraph/label whose content is just
  // text or inline formatting). Only containers get the absolute overlay icon;
  // on a text element the overlay would sit on top of the words, so those keep
  // the in-flow icon that trails the text.
  private isLayoutContainer(el: HTMLElement): boolean {
    for (const child of Array.from(el.children)) {
      if (child.classList.contains('tour-guide-question-icon')) continue;
      const d = window.getComputedStyle(child as HTMLElement).display;
      if (d !== 'inline' && d !== 'contents' && d !== 'none') return true;
    }
    return false;
  }

  private isOverlayTarget(el: HTMLElement): boolean {
    return this.isBlockLevelDisplay(el) && this.isLayoutContainer(el);
  }

  private isVerticalPlacement(placement?: TooltipPosition): boolean {
    return placement === TooltipPosition.Top || placement === TooltipPosition.Bottom;
  }

  // An inline-mode icon is placed as an absolute overlay when the target is a
  // layout container (so the icon takes no layout space), and also for Top/Bottom
  // placement on any target that can host it: stacking above/below in the flow
  // stops working the moment the target sits in a flex row, where the icon just
  // becomes another flex item on the same line no matter what `display` it gets.
  private shouldOverlayInlineIcon(targetElement: HTMLElement, config: TooltipConfig): boolean {
    if ((config.iconPlacementMode ?? TooltipIconMode.Inline) !== TooltipIconMode.Inline) return false;
    if (this.isOverlayTarget(targetElement)) return true;
    return this.isVerticalPlacement(config.iconPlacement)
      && !this.nonHostingTags.has(targetElement.tagName);
  }

  // Initial overlay setup: give the target a containing block, then place the icon.
  private applyOverlayIcon(el: HTMLElement, target: HTMLElement, config: TooltipConfig): void {
    if (window.getComputedStyle(target).position === 'static') {
      this.renderer.setStyle(target, 'position', 'relative');
      this.renderer.setAttribute(target, 'data-tour-guide-relative', '');
    }
    this.applyOverlayPlacement(el, target, config);
  }

  // Position the icon as an absolute overlay pinned to one edge/corner of the
  // target's box, via %-based offsets so it tracks the target's size and scroll
  // with no JS. Used for layout-container targets in Inline mode so the icon
  // never occupies layout space inside the element.
  private applyOverlayPlacement(el: HTMLElement, target: HTMLElement, config: TooltipConfig): void {
    const placement  = config.iconPlacement  ?? TooltipPosition.Right;
    const align      = config.iconAlign      ?? TooltipAlignment.Center;
    const offsetTop  = config.iconOffsetTop  ?? 0;
    const offsetLeft = config.iconOffsetLeft ?? 0;
    const size       = config.iconSize ?? this.iconSize;

    this.applyIconSize(el, config);
    el.style.setProperty('--tour-guide-icon-rotate', `${(config.iconRotation ?? 0) % 360}deg`);

    const EDGE_GAP = 10; // inset from the pinned edge when the icon fits inside
    const OUT_GAP  = 6;  // gap beyond the edge when it doesn't

    // Small wrappers (a pill toggle, a compact button) have no room for the icon
    // inside without covering their content — pin it just OUTSIDE the placement
    // edge instead. Only the placement axis moves outside; the alignment axis
    // still runs along the edge. A text target gets an overlay icon only for
    // Top/Bottom placement, where the whole point is to sit above/below the
    // words, so that case is always outside regardless of how roomy the box is.
    const rect = target.getBoundingClientRect();
    const placeOutside = !this.isLayoutContainer(target)
      || rect.height < size * 2 || rect.width < size * 2.5;

    // along-the-edge axis (alignment): always inside
    const alignAxis = (edge: 'start' | 'end' | 'center', o: number): string =>
      edge === 'center' ? `calc(50% - ${size / 2}px + ${o}px)`
      : edge === 'start' ? `${EDGE_GAP + o}px`
      : `calc(100% - ${size + EDGE_GAP}px + ${o}px)`;

    // pinned edge axis (placement): inside with EDGE_GAP, or outside with OUT_GAP
    const placeAxis = (edge: 'start' | 'end', o: number): string =>
      placeOutside
        ? (edge === 'start' ? `${-(size + OUT_GAP) + o}px` : `calc(100% + ${OUT_GAP}px + ${o}px)`)
        : (edge === 'start' ? `${EDGE_GAP + o}px` : `calc(100% - ${size + EDGE_GAP}px + ${o}px)`);

    let top: string;
    let left: string;
    if (placement === TooltipPosition.Left || placement === TooltipPosition.Right) {
      left = placeAxis(placement === TooltipPosition.Left ? 'start' : 'end', offsetLeft);
      top  = alignAxis(align === TooltipAlignment.Top ? 'start'
                     : align === TooltipAlignment.Bottom ? 'end' : 'center', offsetTop);
    } else {
      top  = placeAxis(placement === TooltipPosition.Top ? 'start' : 'end', offsetTop);
      left = alignAxis(align === TooltipAlignment.Left ? 'start'
                     : align === TooltipAlignment.Right ? 'end' : 'center', offsetLeft);
    }

    this.renderer.setStyle(el, 'position', 'absolute');
    this.renderer.setStyle(el, 'margin', '0');
    this.renderer.setStyle(el, 'top', top);
    this.renderer.setStyle(el, 'left', left);
    this.renderer.removeStyle(el, 'right');
    this.renderer.removeStyle(el, 'bottom');
    this.renderer.removeStyle(el, 'vertical-align');
  }

  private revertTargetPosition(instance: TooltipInstance): void {
    if (!instance.restoredTargetPosition) return;
    instance.restoredTargetPosition = false;
    const host = instance.iconHost;
    const isHostStillUsed = Array.from(this.tooltipInstances.values()).some(other =>
      other !== instance && other.restoredTargetPosition && other.iconHost === host);
    if (isHostStillUsed) return;
    this.renderer.removeStyle(host, 'position');
    this.renderer.removeAttribute(host, 'data-tour-guide-relative');
  }

  // The variable sizes the "?" glyph; width/height are set inline as well because
  // inline styles beat any stylesheet rule regardless of specificity — e.g.
  // Bootstrap's ".row > *" (equal specificity, loaded after our own styles) would
  // otherwise stretch the icon to 100% width as a direct child of a Bootstrap row.
  // Every placement path calls this, so a size change resizes the circle too.
  private applyIconSize(questionMark: HTMLElement, config: TooltipConfig): void {
    const size = config.iconSize ?? this.iconSize;
    questionMark.style.setProperty('--tour-guide-icon-size', `${size}px`);
    this.renderer.setStyle(questionMark, 'width', `${size}px`);
    this.renderer.setStyle(questionMark, 'height', `${size}px`);
    this.renderer.setStyle(questionMark, 'max-width', `${size}px`);
    this.renderer.setStyle(questionMark, 'flex', '0 0 auto');
  }

  private applyInlineStyles(questionMark: HTMLElement, config: TooltipConfig, overlayTarget?: HTMLElement): void {
    if (overlayTarget) {
      this.applyOverlayPlacement(questionMark, overlayTarget, config);
      return;
    }
    const placement   = config.iconPlacement ?? TooltipPosition.Right;
    const align       = config.iconAlign     ?? TooltipAlignment.Center;
    const rotation    = (config.iconRotation ?? 0) % 360;
    const offsetTop   = config.iconOffsetTop  ?? 0;
    const offsetLeft  = config.iconOffsetLeft ?? 0;
    const insertBefore = this.inlineSide(placement) === 'before';
    const isVerticalPlacement = placement === TooltipPosition.Top || placement === TooltipPosition.Bottom;
    this.applyIconSize(questionMark, config);

    this.renderer.setStyle(questionMark, 'top',  offsetTop  !== 0 ? `${offsetTop}px`  : '0px');
    this.renderer.setStyle(questionMark, 'left', offsetLeft !== 0 ? `${offsetLeft}px` : '0px');
    this.renderer.removeStyle(questionMark, 'margin-left');
    this.renderer.removeStyle(questionMark, 'margin-right');

    if (isVerticalPlacement) {
      this.renderer.setStyle(questionMark, 'display', 'flex');
      this.renderer.removeStyle(questionMark, 'vertical-align');
      this.renderer.setStyle(questionMark, 'margin-left', align === TooltipAlignment.Left ? '0' : 'auto');
      this.renderer.setStyle(questionMark, 'margin-right', align === TooltipAlignment.Right ? '0' : 'auto');
    } else {
      const vertAlign = align === TooltipAlignment.Top    ? 'top'
                      : align === TooltipAlignment.Bottom ? 'bottom'
                      : 'middle';
      this.renderer.setStyle(questionMark, 'display', 'inline-flex');
      this.renderer.setStyle(questionMark, 'vertical-align', vertAlign);
      this.renderer.setStyle(questionMark, insertBefore ? 'margin-right' : 'margin-left', '8px');
    }
    questionMark.style.setProperty('--tour-guide-icon-rotate', `${rotation}deg`);
  }

  private repositionAllIcons(): void {
    this.tooltipInstances.forEach(instance => {
      this.positionQuestionMarkIcon(instance.questionMarkElement, instance.targetElement, instance.config);
    });
  }

  private showTooltip(config: TooltipConfig, targetElement: HTMLElement): void {
    // Already open: tear it down first so a second call can't orphan the live
    // component (its DOM node would stay in <body> with nothing left to close
    // it). A second click on the *same* icon just toggles it shut.
    if (this.isTooltipOpen) {
      const sameIcon = this.currentTooltipConfig?.id === config.id;
      this.hideTooltip();
      if (sameIcon) return;
    }

    const highlightElement = this.resolveHighlightElement(config, targetElement);

    this.isTooltipOpen = true;
    this.tooltipInstances.forEach(instance => {
      this.renderer.setStyle(instance.questionMarkElement, 'z-index', String(this.zIndex.QUESTION_MARK_WHEN_TOOLTIP_OPEN));
    });

    this.unlistenOpenTooltipListeners.push(this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('app-tour-guide-tooltip')) return;
      if (target.closest('.tour-guide-question-icon')) return;
      if (target.closest('.tooltip-admin-toolbar')) return;
      this.hideTooltip();
    }));

    this.showHighlightOverlay(highlightElement, config);

    const positioningElement = this.resolvePositioningElement(config, highlightElement);
    this.currentPositioningElement = positioningElement;
    this.currentHighlightedElement = highlightElement;
    this.currentTooltipConfig = config;

    this.createTooltipComponent(config, positioningElement);
    this.startOpenTooltipResizeTracking(positioningElement);

    // Icon-aligned tooltips position the bubble off the icon's own rect —
    // hiding it mid-open would pull that reference out from under itself, so
    // the setting only takes effect for container-aligned tooltips (also
    // gated in the admin UI, but re-checked here defensively).
    if (config.hideIconAfterOpen && config.tooltipAlignTarget !== TooltipAlignTarget.Icon) {
      const iconEl = this.tooltipInstances.get(config.id)?.questionMarkElement;
      if (iconEl) {
        this.renderer.addClass(iconEl, 'tour-guide-question-icon-hidden-after-open');
        this.hiddenIconElement = iconEl;
      }
    }
  }

  private resolveHighlightElement(config: TooltipConfig, targetElement: HTMLElement): HTMLElement {
    if (!config.hoverAnchor) return targetElement;
    const cached = document.querySelector(`[data-tooltip-hover-id="${config.id}"]`) as HTMLElement | null;
    if (cached) return cached;
    return this.anchorService.resolveAnchor(config.hoverAnchor, config.id, 'data-tooltip-hover-id', true) ?? targetElement;
  }

  // The highlight overlay always outlines the picked hover container (or the
  // anchor target, when there's none) — that's "what this tooltip is about".
  // The bubble's position is a separate concern: by default it's relative to
  // that same element, but TooltipAlignTarget.Icon lets the admin pin it to
  // the icon glyph's own rect instead (e.g. an overlay icon pinned to a
  // corner sits far from its container's center).
  private resolvePositioningElement(config: TooltipConfig, highlightElement: HTMLElement): HTMLElement {
    if (config.tooltipAlignTarget === TooltipAlignTarget.Icon) {
      const iconEl = this.tooltipInstances.get(config.id)?.questionMarkElement;
      if (iconEl) return iconEl;
    }
    return highlightElement;
  }

  private repositionOpenTooltip(): void {
    if (!this.isTooltipOpen || !this.currentTooltipConfig || !this.currentPositioningElement) return;

    const rect = this.currentPositioningElement.getBoundingClientRect();
    const inViewport = rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;

    if (!inViewport) {
      if (this.highlightOverlayElement) this.renderer.setStyle(this.highlightOverlayElement, 'display', 'none');
      if (this.tooltipComponentRef) {
        const domElem = (this.tooltipComponentRef.hostView as any).rootNodes[0] as HTMLElement;
        this.renderer.setStyle(domElem, 'display', 'none');
      }
      return;
    }

    if (this.currentHighlightedElement) this.showHighlightOverlay(this.currentHighlightedElement, this.currentTooltipConfig);
    // Runs every frame while tracking — only move the bubble. Content and
    // config changes go through refreshOpenTooltip (from updateAllTooltips).
    if (!this.tooltipComponentRef) return;
    const domElem = (this.tooltipComponentRef.hostView as any).rootNodes[0] as HTMLElement;
    this.positionTooltip(domElem, this.currentPositioningElement, this.currentTooltipConfig);
  }

  private startOpenTooltipResizeTracking(target: HTMLElement): void {
    this.stopOpenTooltipResizeTracking();

    if (typeof ResizeObserver === 'undefined') return;

    try {
      this.openTooltipResizeObserver = new ResizeObserver(() => this.markMovementActive());
      this.openTooltipResizeObserver.observe(target);
    } catch (e) {
      console.error('ResizeObserver initialization failed for open tooltip:', e);
    }
  }

  private stopOpenTooltipResizeTracking(): void {
    if (this.openTooltipResizeObserver) {
      this.openTooltipResizeObserver.disconnect();
      this.openTooltipResizeObserver = null;
    }
  }

  private showHighlightOverlay(target: HTMLElement, config: TooltipConfig): void {
    // Toggled off entirely — no overlay element at all, not just an invisible one.
    if (config.showContainerBorder === false) {
      this.removeHighlightOverlay();
      return;
    }

    const rect = target.getBoundingClientRect();
    const borderWidth = config.containerBorderWidth ?? this.defaultContainerBorderWidth;
    const borderColor = config.containerBorderColor ?? this.defaultContainerBorderColor;

    // Reused across calls instead of torn down and recreated every time —
    // this now runs on every tracking-loop frame while scrolling, and
    // recreating the DOM node that often is expensive and looks janky.
    if (!this.highlightOverlayElement) {
      this.highlightOverlayElement = this.renderer.createElement('div');
      this.renderer.addClass(this.highlightOverlayElement, 'tour-guide-highlighted-overlay');
      this.renderer.appendChild(document.body, this.highlightOverlayElement);
    }

    this.renderer.removeStyle(this.highlightOverlayElement, 'display');
    this.renderer.setStyle(this.highlightOverlayElement, 'top', `${rect.top}px`);
    this.renderer.setStyle(this.highlightOverlayElement, 'left', `${rect.left}px`);
    this.renderer.setStyle(this.highlightOverlayElement, 'width', `${rect.width}px`);
    this.renderer.setStyle(this.highlightOverlayElement, 'height', `${rect.height}px`);
    this.renderer.setStyle(this.highlightOverlayElement, 'box-shadow', `0 0 0 ${borderWidth}px ${borderColor}`);
    this.renderer.setStyle(this.highlightOverlayElement, 'z-index', String(this.zIndex.HIGHLIGHTED_ELEMENT));
  }

  private removeHighlightOverlay(): void {
    if (this.highlightOverlayElement) {
      if (this.highlightOverlayElement.parentNode) {
        this.renderer.removeChild(this.highlightOverlayElement.parentNode, this.highlightOverlayElement);
      }
      this.highlightOverlayElement = null;
    }
  }

  private createTooltipComponent(config: TooltipConfig, targetElement: HTMLElement): void {
    this.tooltipComponentRef = createComponent(TourGuideTooltipComponent, {
      environmentInjector: this.environmentInjector,
      elementInjector: this.injector
    });

    this.tooltipComponentRef.instance.text = config.text;
    this.tooltipComponentRef.instance.header = config.header;
    this.tooltipComponentRef.instance.position = Number(config.position) as TooltipPosition;

    this.tooltipComponentRef.instance.closeTooltip.subscribe(() => {
      this.hideTooltip();
    });

    this.appRef.attachView(this.tooltipComponentRef.hostView);
    this.tooltipComponentRef.changeDetectorRef.detectChanges();

    const domElem = (this.tooltipComponentRef.hostView as any).rootNodes[0] as HTMLElement;

    const tooltipInner = domElem.querySelector('.tour-guide-tooltip');
    if (tooltipInner) {
      this.renderer.setAttribute(tooltipInner, 'data-tooltip-align', TooltipAlignment[config.tooltipAlign ?? TooltipAlignment.Center].toLowerCase());
    }

    this.renderer.appendChild(document.body, domElem);

    setTimeout(() => {
      this.positionTooltip(domElem, targetElement, config);
    }, 0);
  }

  private refreshOpenTooltip(config: TooltipConfig): void {
    if (!this.tooltipComponentRef || !this.currentPositioningElement || !this.currentHighlightedElement) return;

    // The align target itself can change while the tooltip is open (live
    // admin preview) — re-resolve which element the bubble tracks, and
    // restart resize tracking if that element changed.
    const positioningElement = this.resolvePositioningElement(config, this.currentHighlightedElement);
    if (positioningElement !== this.currentPositioningElement) {
      this.currentPositioningElement = positioningElement;
      this.startOpenTooltipResizeTracking(positioningElement);
    }

    // Border toggle/width/color are also part of the live preview.
    this.showHighlightOverlay(this.currentHighlightedElement, config);

    this.tooltipComponentRef.instance.text = config.text;
    this.tooltipComponentRef.instance.header = config.header;
    this.tooltipComponentRef.instance.position = Number(config.position) as TooltipPosition;
    this.tooltipComponentRef.changeDetectorRef.detectChanges();

    const domElem = (this.tooltipComponentRef.hostView as any).rootNodes[0] as HTMLElement;
    const tooltipInner = domElem.querySelector('.tour-guide-tooltip');
    if (tooltipInner) {
      this.renderer.setAttribute(tooltipInner, 'data-tooltip-align', TooltipAlignment[config.tooltipAlign ?? TooltipAlignment.Center].toLowerCase());
    }

    this.positionTooltip(domElem, this.currentPositioningElement, config);
  }

  private positionTooltip(tooltipElement: HTMLElement, targetElement: HTMLElement, config: TooltipConfig): void {
    const targetRect = targetElement.getBoundingClientRect();
    const innerTooltip = tooltipElement.querySelector('.tour-guide-tooltip') as HTMLElement;
    const tooltipRect = innerTooltip ? innerTooltip.getBoundingClientRect() : tooltipElement.getBoundingClientRect();
    const tooltipAlign = config.tooltipAlign ?? TooltipAlignment.Center;
    const position = config.position;
    const { top, left, finalPosition, shiftX, shiftY } = this.computeTooltipPlacement(targetRect, tooltipRect, config);

    this.setSmartPositionedTooltip(config.id, finalPosition !== position);

    this.renderer.setStyle(tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(tooltipElement, 'left', `${left}px`);
    this.renderer.setStyle(tooltipElement, 'visibility', 'visible');
    this.renderer.setStyle(tooltipElement, 'opacity', '1');
    this.renderer.setStyle(tooltipElement, 'display', 'block');
    this.renderer.setStyle(tooltipElement, 'position', 'fixed');
    this.renderer.setStyle(tooltipElement, 'z-index', String(this.zIndex.TOOLTIP));

    if (innerTooltip) {
      this.renderer.setAttribute(innerTooltip, 'data-position', TooltipPosition[finalPosition].toLowerCase());
      const arrowRatio = tooltipAlign === TooltipAlignment.Left || tooltipAlign === TooltipAlignment.Top ? 0.2
                       : tooltipAlign === TooltipAlignment.Right || tooltipAlign === TooltipAlignment.Bottom ? 0.8
                       : 0.5;
      // When the bubble was pushed back inside the viewport, move the arrow back
      // by the same amount so it keeps pointing at the icon, but never onto the
      // bubble's rounded corners.
      const isArrowOnHorizontalEdge = finalPosition === TooltipPosition.Top || finalPosition === TooltipPosition.Bottom;
      const edgeLength = isArrowOnHorizontalEdge ? tooltipRect.width : tooltipRect.height;
      const viewportShift = isArrowOnHorizontalEdge ? shiftX : shiftY;
      const arrowOffset = Math.min(
        Math.max(edgeLength * arrowRatio - viewportShift, this.tooltipArrowEdgeInset),
        edgeLength - this.tooltipArrowEdgeInset
      );
      innerTooltip.style.setProperty('--tour-guide-arrow-offset', `${arrowOffset}px`);
    }
  }

  // Where the bubble goes for a given target/bubble size, including the
  // "smart" flip to the opposite side when the configured one doesn't fit.
  // shiftX/shiftY: how far the final viewport clamp moved the bubble.
  private computeTooltipPlacement(
    targetRect: DOMRect,
    tooltipRect: DOMRect,
    config: TooltipConfig
  ): { top: number; left: number; finalPosition: TooltipPosition; shiftX: number; shiftY: number } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gap = 12;
    const padding = 10;

    const tooltipAlign = config.tooltipAlign ?? TooltipAlignment.Center;
    const position = config.position;

    let top = 0;
    let left = 0;
    let finalPosition: TooltipPosition = position;

    const getHorizontalAlign = () => {
      const pct = tooltipAlign === TooltipAlignment.Left ? 0.1
                : tooltipAlign === TooltipAlignment.Right ? 0.9
                : 0.5;
      const raw = targetRect.left + targetRect.width * pct - tooltipRect.width / 2;
      if (tooltipAlign === TooltipAlignment.Left) return Math.max(raw, targetRect.left);
      if (tooltipAlign === TooltipAlignment.Right) return Math.min(raw, targetRect.right - tooltipRect.width);
      return raw;
    };

    const getVerticalAlign = () => {
      const pct = tooltipAlign === TooltipAlignment.Top ? 0.1
                : tooltipAlign === TooltipAlignment.Bottom ? 0.9
                : 0.5;
      const raw = targetRect.top + targetRect.height * pct - tooltipRect.height / 2;
      if (tooltipAlign === TooltipAlignment.Top) return Math.max(raw, targetRect.top);
      if (tooltipAlign === TooltipAlignment.Bottom) return Math.min(raw, targetRect.bottom - tooltipRect.height);
      return raw;
    };

    switch (position) {
      case TooltipPosition.Top:
        top = targetRect.top - tooltipRect.height - gap;
        left = getHorizontalAlign();
        if (top < padding) { finalPosition = TooltipPosition.Bottom; top = targetRect.bottom + gap; }
        break;
      case TooltipPosition.Bottom:
        top = targetRect.bottom + gap;
        left = getHorizontalAlign();
        if (top + tooltipRect.height > viewportHeight - padding) { finalPosition = TooltipPosition.Top; top = targetRect.top - tooltipRect.height - gap; }
        break;
      case TooltipPosition.Left:
        top = getVerticalAlign();
        left = targetRect.left - tooltipRect.width - gap;
        if (left < padding) { finalPosition = TooltipPosition.Right; left = targetRect.right + gap; }
        break;
      case TooltipPosition.Right:
      default:
        top = getVerticalAlign();
        left = targetRect.right + gap;
        if (left + tooltipRect.width > viewportWidth - padding) { finalPosition = TooltipPosition.Left; left = targetRect.left - tooltipRect.width - gap; }
        break;
    }

    const unclampedLeft = left;
    const unclampedTop = top;

    if (left < padding) left = padding;
    else if (left + tooltipRect.width > viewportWidth - padding) left = viewportWidth - tooltipRect.width - padding;

    if (top < padding) top = padding;
    else if (top + tooltipRect.height > viewportHeight - padding) top = viewportHeight - tooltipRect.height - padding;

    return { top, left, finalPosition, shiftX: left - unclampedLeft, shiftY: top - unclampedTop };
  }

  private setSmartPositionedTooltip(id: string, smart: boolean): void {
    const current = this.smartPositionedTooltipId$.value;
    if (smart && current !== id) this.smartPositionedTooltipId$.next(id);
    else if (!smart && current === id) this.smartPositionedTooltipId$.next(null);
  }

  // Predicts whether this tooltip would be smart-positioned if it were opened
  // right now, without opening it: renders an invisible copy of the bubble to
  // measure it, then runs the same placement math as positionTooltip. While
  // this tooltip is actually open, positionTooltip's live result wins.
  checkSmartPosition(config: TooltipConfig): void {
    if (this.isTooltipOpen && this.currentTooltipConfig?.id === config.id) return;

    const targetElement = this.tooltipInstances.get(config.id)?.targetElement;
    if (!targetElement?.isConnected) {
      this.setSmartPositionedTooltip(config.id, false);
      return;
    }

    const positioningElement = this.resolvePositioningElement(config, this.resolveHighlightElement(config, targetElement));

    const measureRef = createComponent(TourGuideTooltipComponent, {
      environmentInjector: this.environmentInjector,
      elementInjector: this.injector
    });
    measureRef.instance.text = config.text;
    measureRef.instance.header = config.header;
    measureRef.instance.position = Number(config.position) as TooltipPosition;
    measureRef.changeDetectorRef.detectChanges();

    const domElem = (measureRef.hostView as any).rootNodes[0] as HTMLElement;
    domElem.style.cssText = 'position:fixed;top:0;left:0;display:block;visibility:hidden;pointer-events:none;';
    const innerTooltip = domElem.querySelector('.tour-guide-tooltip') as HTMLElement | null;
    // The fade-in animation starts at scale(0.96) — measure the final size.
    if (innerTooltip) innerTooltip.style.animation = 'none';
    document.body.appendChild(domElem);

    const tooltipRect = (innerTooltip ?? domElem).getBoundingClientRect();
    const { finalPosition } = this.computeTooltipPlacement(positioningElement.getBoundingClientRect(), tooltipRect, config);

    domElem.remove();
    measureRef.destroy();

    this.setSmartPositionedTooltip(config.id, finalPosition !== config.position);
  }

  private hideTooltip(): void {
    this.stopOpenTooltipResizeTracking();
    this.unlistenOpenTooltipListeners.forEach(unlisten => unlisten());
    this.unlistenOpenTooltipListeners = [];

    this.removeHighlightOverlay();
    this.currentHighlightedElement = null;

    if (this.hiddenIconElement) {
      this.renderer.removeClass(this.hiddenIconElement, 'tour-guide-question-icon-hidden-after-open');
      this.hiddenIconElement = null;
    }

    this.isTooltipOpen = false;
    this.tooltipInstances.forEach(instance => {
      this.renderer.setStyle(instance.questionMarkElement, 'z-index', String(this.zIndex.QUESTION_MARK));
    });

    if (this.tooltipComponentRef) {
      this.appRef.detachView(this.tooltipComponentRef.hostView);
      this.tooltipComponentRef.destroy();
      this.tooltipComponentRef = null;
    }

    this.currentPositioningElement = null;
    this.currentTooltipConfig = null;
  }

  private removeTooltipInstance(id: string): void {
    const instance = this.tooltipInstances.get(id);
    if (instance) {
      if (instance.resizeObserver) {
        instance.resizeObserver.disconnect();
        instance.resizeObserver = null;
      }
      if (instance.targetElement) {
        instance.targetElement.removeAttribute('data-tooltip-id');
      }
      this.revertTargetPosition(instance);
      if (instance.questionMarkElement.parentNode) {
        this.renderer.removeChild(instance.questionMarkElement.parentNode, instance.questionMarkElement);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  public destroy(): void {
    if (this.unlistenWindowResize) {
      this.unlistenWindowResize();
      this.unlistenWindowResize = null;
    }
    if (this.unlistenDocumentScroll) {
      this.unlistenDocumentScroll();
      this.unlistenDocumentScroll = null;
    }
    this.stopTrackingLoop();
    this.stopLayoutTransitionObserver();
    this.tooltipInstances.forEach((instance, id) => {
      if (instance.resizeObserver) {
        instance.resizeObserver.disconnect();
        instance.resizeObserver = null;
      }
      this.removeTooltipInstance(id);
    });
    this.tooltipInstances.clear();
    this.hideTooltip();
  }
}
